/**
 * Scrape Now popup (PRD §1, §5.1 #16; redesign § Dialogs).
 *
 * Lists every group and every active profile with checkboxes, a name filter and select/deselect
 * all. Selecting a group implies its members, so the dialog shows the **deduplicated union** count
 * before you start — the server dedupes it again for real.
 *
 * "Start" fires the mutation and **closes immediately** (redesign [D]) — runs continue in the
 * background and the home rows update live; any failure surfaces through the existing status /
 * banner paths rather than in this dialog.
 */

import { useMemo, useState } from 'react';
import type { GroupDTO, ProfileRowDTO } from '@lw/shared';
import { CheckboxList, Modal } from '../../components';
import { pluralize } from '../../lib/format';
import { useStartScrape } from './useHomeData';

export type ScrapeNowDialogProps = {
  open: boolean;
  profiles: ProfileRowDTO[];
  groups: GroupDTO[];
  onClose: () => void;
};

export function ScrapeNowDialog({ open, profiles, groups, onClose }: ScrapeNowDialogProps) {
  const [filter, setFilter] = useState('');
  const [profileIds, setProfileIds] = useState<number[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const scrape = useStartScrape();

  const active = useMemo(
    () => profiles.filter((profile) => (profile.archivedAt ?? null) === null),
    [profiles],
  );

  const needle = filter.trim().toLowerCase();
  const visibleProfiles = useMemo(
    () =>
      needle.length === 0
        ? active
        : active.filter((profile) =>
            `${profile.name ?? ''} ${profile.slug}`.toLowerCase().includes(needle),
          ),
    [active, needle],
  );
  const visibleGroups = useMemo(
    () =>
      needle.length === 0
        ? groups
        : groups.filter((group) => group.name.toLowerCase().includes(needle)),
    [groups, needle],
  );

  /** The union the server will act on: explicit profiles plus every selected group's members. */
  const union = useMemo(() => {
    const ids = new Set<number>(profileIds);
    const activeIds = new Set(active.map((profile) => profile.id));
    for (const group of groups) {
      if (!groupIds.includes(group.id)) continue;
      for (const memberId of group.memberIds) if (activeIds.has(memberId)) ids.add(memberId);
    }
    return ids;
  }, [profileIds, groupIds, groups, active]);

  const close = () => {
    setProfileIds([]);
    setGroupIds([]);
    setFilter('');
    onClose();
  };

  const start = () => {
    if (union.size === 0) return;
    // Fire and close — never wait for the response (redesign § Dialogs).
    scrape.mutate({
      profileIds: profileIds.length > 0 ? profileIds : undefined,
      groupIds: groupIds.length > 0 ? groupIds : undefined,
    });
    close();
  };

  return (
    <Modal
      open={open}
      title="Scrape now"
      size="md"
      onClose={close}
      footer={
        <>
          <span className="muted dialog-hint">
            {union.size === 0
              ? 'Nothing selected'
              : `${pluralize(union.size, 'profile')} will be scraped`}
          </span>
          <button type="button" className="table-action" onClick={close}>
            <span className="table-action__label">Close</span>
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={start}
            disabled={union.size === 0}
          >
            Start
          </button>
        </>
      }
    >
      <div className="scrape-now">
        <div className="field">
          <label className="field__label" htmlFor="scrape-now-filter">
            Filter by name
          </label>
          <input
            id="scrape-now-filter"
            className="input"
            value={filter}
            placeholder="Type to narrow the lists"
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>

        <div className="scrape-now__cols">
          <section>
            <h3 className="scrape-now__title">Groups</h3>
            <CheckboxList
              options={visibleGroups.map((group) => ({
                value: group.id,
                label: group.name,
                hint: pluralize(group.memberCount, 'member'),
              }))}
              selected={groupIds}
              onChange={setGroupIds}
              withBulkActions
              emptyLabel="No groups match."
            />
          </section>

          <section>
            <h3 className="scrape-now__title">Profiles</h3>
            <CheckboxList
              options={visibleProfiles.map((profile) => ({
                value: profile.id,
                label: profile.name ?? profile.slug,
                hint:
                  union.has(profile.id) && !profileIds.includes(profile.id) ? 'via group' : undefined,
              }))}
              selected={profileIds}
              onChange={setProfileIds}
              withBulkActions
              emptyLabel="No profiles match."
            />
          </section>
        </div>
      </div>
    </Modal>
  );
}

export default ScrapeNowDialog;
